"use client";
//? Library Imports ------------------------------------------------------------>
import { Col, Row, Spin, Space, Drawer, Typography } from "antd";
import Image from "next/image";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useCallback } from "react";
import { usePathname, useRouter } from "next/navigation";
import { AiFillCloseCircle } from "react-icons/ai";

//? Assets --------------------------------------------------------------------->
import "./CartDrawer.scss";
import CartIcon from "../../../../assets/Icons/CartIcon.svg";
import EmptyBag from "../../../../assets/images/CartPage/empty-cart.png";
import ArrowDown from "../../../../assets/images/CartPage/ArrowDown.svg";
import defaultImage from "../../../../assets/images/defaultImages/alfa-loader.gif";

//? Modules -------------------------------------------------------------------->
import DynamicButton from "@/components/shared/Buttons/DynamicButton/DynamicButton";

//? Redux Reducers ------------------------------------------------------------->
import {
  displayCart,
  updateCartCount,
  clearCart,
  updateCart,
} from "@/redux/Slices/cartSlice";
import { clearCheckout } from "@/redux/Slices/checkoutSlice";
import {
  clearCartDeliveryCountryCookie,
  getLineItemDeliveryCountry,
} from "@/functions/HelperFunctions/index";

const { Text } = Typography;

export default function CartDrawer() {
  const dispatch = useDispatch();
  const pathname = usePathname();
  const router = useRouter();

  //? Theme: fashion = black, alfamall = red
  const isFashionPath = pathname?.includes("fashion");
  const themeColor = isFashionPath ? "#000000" : "#d62314";

  //? Cookies -------------------------------------------------------------------->
  let cartId = Cookies.get("cartId");
  const fashionCartId = Cookies.get("fashion_cart_id");
  const defaultCurrencyIdFromCookies = Cookies.get("defaultCurrencyId");
  const defaultCurrency = Cookies.get("defaultCurrencyName");
  const showCartDropdown = useSelector((state) => state?.cartSlice?.showCart);

  //? States --------------------------------------------------------------------->
  const [isOpen, setIsOpen] = useState(false);
  const [cartData, setCartData] = useState(null);
  const [loading, setLoading] = useState(false);

  //? Helpers -------------------------------------------------------------------->
  const getOptionTitle = (options, position) =>
    options.find((opt) => opt.position === position)?.name ?? "Size";

  const handleClose = () => {
    setIsOpen(false);
    dispatch(displayCart(false));
  };

  const handleCheckoutNavigation = () => {
    handleClose();
    const useCartId = isFashionPath
      ? fashionCartId || cartId
      : cartId || fashionCartId;

    if (isFashionPath && useCartId) {
      router.push(`/fashion/cart/checkout?id=${useCartId}`);
    } else if (useCartId) {
      router.push(`/cart/checkout?id=${useCartId}`);
    }
  };

  const clearCartData = () => {
    dispatch(clearCheckout());
    dispatch(clearCart());
    dispatch(updateCart());
    dispatch(updateCartCount(0));
    Cookies.remove("cartId");
    Cookies.remove("fashion_cart_id");
    clearCartDeliveryCountryCookie();
  };

  const fetchCartProducts = useCallback(async () => {
    const useCartId = isFashionPath
      ? fashionCartId || cartId
      : cartId || fashionCartId;

    if (!useCartId) {
      dispatch(updateCartCount(0));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const storeId = defaultCurrencyIdFromCookies || "23";
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND}/order/view_cart?storefront=${storeId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json", cartid: useCartId },
        },
      );
      const data = await res.json();

      if (!res.ok) {
        if (data.detail === "cart not found") clearCartData();
        throw new Error("ViewCart API Failed!");
      }
      setCartData(data);
    } catch (error) {
      console.error("CartDrawer fetch error:", error.message);
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  }, [cartId, fashionCartId, isFashionPath, defaultCurrencyIdFromCookies]);

  //? Effects -------------------------------------------------------------------->
  useEffect(() => {
    if ((cartId || fashionCartId) && isOpen) fetchCartProducts();
  }, [cartId, fashionCartId, isOpen, fetchCartProducts]);

  useEffect(() => {
    if (showCartDropdown) setIsOpen(true);
  }, [showCartDropdown]);

  return (
    <Col span={24} className="CartDrawerWrapper">
      {/* Trigger Icon */}
      <Col
        onClick={() => setIsOpen(!isOpen)}
        className="flexColumn flexCenter cursorPointer"
      >
        <Image height={22} width="auto" alt="cart" src={CartIcon} />
      </Col>


      {/* Drawer */}
      <Drawer
        open={isOpen}
        onClose={handleClose}
        placement="right"
        width={420}
        closable={false}
        className={`cart-drawer ${isFashionPath ? "cart-drawer--fashion" : "cart-drawer--alfa"}`}
        footer={
          cartData?.line_items?.length ? (
            <DynamicButton
              name="View Cart"
              function={handleCheckoutNavigation}
              style={{
                height: "49px",
                width: "100%",
                fontWeight: 600,
                fontSize: "16px",
                borderRadius: "6px",
                backgroundColor: themeColor,
                borderColor: themeColor,
                color: "#fff",
              }}
            />
          ) : null
        }
        title={
          <div className="cart-drawer__header">
            <Text className="font18Bold" style={{ color: themeColor }}>
              Cart
            </Text>
            <AiFillCloseCircle
              style={{ fontSize: "22px", cursor: "pointer", color: themeColor }}
              onClick={handleClose}
            />
          </div>
        }
      >
        <Spin spinning={loading}>
          {cartData?.line_items?.length ? (
            <Row justify="center">
              {/* Cart Items */}
              <Col span={24} className="CartDrawerItems scroll">
                {cartData.line_items.map((cartItem, index) => {
                  const selectedAddons = cartItem.add_on_templates.flatMap(
                    (template) =>
                      template.add_ons.flatMap((addon) =>
                        addon.options
                          .filter((option) =>
                            cartItem.selected_add_ons.some(
                              (sel) => sel.option === option.id,
                            ),
                          )
                          .map((option) => ({ ...addon, selected_option: option })),
                      ),
                  );

                  const size = selectedAddons.find(
                    (a) => a?.name.toLowerCase() === "size",
                  );

                  const selectedVariant = cartItem.variants.find(
                    (v) =>
                      v?.variant_detail?.variant_id ===
                      cartItem.variant_price_inventory,
                  );

                  return (
                    <Col span={24} key={index} className="padding10">
                      <Space
                        className="both"
                        style={{ width: "100%", alignItems: "flex-start" }}
                      >
                        <Image
                          width={120}
                          height={160}
                          alt="Cart Product"
                          src={cartItem?.img_url || defaultImage}
                        />
                        <Space
                          className="gap10"
                          direction="vertical"
                          style={{ width: "100%" }}
                        >
                          <Text className="font14Bold">
                            SKU: {cartItem?.sku || "N/A"}
                          </Text>
                          <Text className="name font14">
                            {cartItem?.title || "N/A"}
                          </Text>
                          {getLineItemDeliveryCountry(cartItem) ? (
                            <Text className="font12" style={{ color: "#666" }}>
                              Country: {getLineItemDeliveryCountry(cartItem)}
                            </Text>
                          ) : null}

                          {/* Option 1 */}
                          {!cartItem?.is_stitch &&
                          !size &&
                          !selectedVariant?.option1.includes("Default") ? (
                            selectedVariant?.option1 && (
                              <Space
                                className="cursorPointer flexBetween"
                                style={{ width: "100%", padding: "5px", background: "#f2f3f7" }}
                              >
                                <Text className="font14Bold">
                                  {getOptionTitle(cartItem.options, 1)}:&nbsp;
                                  <Text>{selectedVariant.option1}</Text>
                                </Text>
                              </Space>
                            )
                          ) : null}

                          {/* Option 2 */}
                          {selectedVariant?.option2 && (
                            <Space
                              className="cursorPointer flexBetween"
                              style={{ width: "100%", padding: "5px", background: "#f2f3f7" }}
                            >
                              <Text className="font14Bold">
                                {getOptionTitle(cartItem.options, 2)}:&nbsp;
                                <Text>{selectedVariant.option2}</Text>
                              </Text>
                            </Space>
                          )}

                          {/* Option 3 */}
                          {selectedVariant?.option3 && (
                            <Space
                              className="cursorPointer flexBetween"
                              style={{ width: "100%", padding: "5px", background: "#f2f3f7" }}
                            >
                              <Text className="font14Bold">
                                {getOptionTitle(cartItem.options, 3)}:&nbsp;
                                <Text>{selectedVariant.option3}</Text>
                              </Text>
                            </Space>
                          )}

                          {/* Quantity */}
                          <Space
                            className="cursorPointer flexBetween"
                            style={{ width: "60px", padding: "5px", background: "#f2f3f7" }}
                          >
                            <Text className="font14Bold">
                              Qty:&nbsp;
                              <span className="font14">
                                {cartItem?.quantity || "N/A"}
                              </span>
                            </Text>
                          </Space>

                          {/* Plan */}
                          <Space
                            className="cursorPointer flexBetween"
                            style={{ width: "100%", padding: "5px", background: "#f2f3f7" }}
                          >
                            <Text className="font14Bold">
                              Plan:&nbsp;
                              {cartItem?.installment_plan || "Pay In Full"}
                            </Text>
                          </Space>

                          {/* Card Type */}
                          {cartItem?.card_type && (
                            <Space
                              className="cursorPointer flexBetween"
                              style={{ width: "150px", padding: "5px", background: "#f2f3f7" }}
                            >
                              <Text className="font14Bold">
                                Card:&nbsp;
                                <span className="font14">
                                  {cartItem.card_type === "all_bank_plans"
                                    ? "Other Bank"
                                    : "Alfalah"}
                                </span>
                              </Text>
                            </Space>
                          )}

                          {/* Add-ons */}
                          {selectedAddons.map((addOn, key) => (
                            <Row
                              key={key}
                              className="cursorPointer"
                              style={{
                                width: "100%",
                                padding: "5px",
                                background: "#f2f3f7",
                                alignItems: "baseline",
                              }}
                            >
                              {addOn?.name && (
                                <Col flex="0 0 auto">
                                  <Text className="font14Bold">
                                    {addOn.name}:&nbsp;
                                  </Text>
                                </Col>
                              )}
                              <Col flex="1 0 auto">
                                <Space className="cursorPointer flexBetween">
                                  <Text>{addOn?.selected_option?.name}</Text>
                                  <Image width={10} src={ArrowDown} alt="ArrowDown" />
                                </Space>
                              </Col>
                            </Row>
                          ))}

                          {/* Price */}
                          <Col flex="1 1 auto">
                            <Text className="font16Bold" style={{ color: themeColor }}>
                              {defaultCurrency}&nbsp;
                              {cartItem?.price || "N/A"}
                            </Text>
                          </Col>
                        </Space>
                      </Space>
                    </Col>
                  );
                })}
              </Col>


            </Row>
          ) : (
            /* Empty Cart */
            <Col className="flexColumn flexCenter" style={{ paddingTop: "40px" }}>
              <Image height={70} width="auto" src={EmptyBag} alt="Empty Cart" />
              <Text
                style={{
                  marginTop: "10px",
                  fontWeight: 100,
                  fontSize: "20px",
                  color: "#000000",
                  fontFamily: "ZemestroStd-Medium",
                }}
              >
                {`Hey! It's lonely here`}
              </Text>
              <Text style={{ color: "#747474", fontFamily: "ZemestroStd-Regular" }}>
                Your cart is empty
              </Text>
              <DynamicButton
                link={isFashionPath ? "/fashion" : "/"}
                name="Shop Now"
                style={{
                  width: "171px",
                  height: "41px",
                  margin: "30px auto 20px auto",
                  backgroundColor: themeColor,
                  borderColor: themeColor,
                  color: "#fff",
                  fontWeight: 600,
                  borderRadius: "6px",
                }}
              />
            </Col>
          )}
        </Spin>
      </Drawer>
    </Col>
  );
}
